import { useState, useEffect, useRef } from 'react'
import { useField } from '@unform/core'
import { IMask } from 'react-imask'
import { Icon } from 'assets/index'
import { loadingOrange } from 'assets/Icons'
import { MaskedInput, Row, Loading } from './style'
import {
  Placeholder,
  InputError,
  InputContainer,
} from '../FormCommomStyle/FormCommomStyle'

interface IInputProps {
  small?: boolean
  large?: boolean
  customWith?: number
  icon?: boolean
  customError?: string
  marginRight?: number
  name: string
  unmask?: boolean
  handleChange?: any
  handleCreated?: (_ref: any, _name: string) => void
  onBlur?: (_ev: any) => void
  initialError?: string
  placeholder?: string
  disabled?: boolean
  defaultValue?: string | number | undefined | null
  type: string
  getUnmaskedValue?: any
  uniqKey?: any
  hasAsterisk?: boolean
  dataTestId: string
  maxLength?: number
  loading?: boolean
  min?: number
  max?: number
  trimestral?: boolean
  // step?: number
}

const getCurrent18 = () => {
  const now = new Date()
  return now.getFullYear() - 18
}

export default function MaskedInputComponent2({
  trimestral,
  name,
  initialError,
  handleChange,
  type,
  placeholder,
  defaultValue,
  uniqKey,
  onBlur,
  disabled,
  hasAsterisk,
  customWith,
  dataTestId,
  large,
  small,
  maxLength,
  loading,
  customError,
  // step = 1,
  min = 0,
  max = 100,
  ...rest
}: IInputProps) {
  const inputRef = useRef<any>(null)
  const [inputError, setInputError] = useState(initialError)
  const { fieldName, registerField, error, clearError } = useField(name)
  const [inputFocused, setInputFocused] = useState(false)
  const [isFocusedOrValid, setIsFocusedOrValid] = useState(
    !!defaultValue || defaultValue === 0
  )

  const handleFocus = () => {
    setInputFocused(true)
    clearError()
  }
  const handleBlur = () => {
    setInputFocused(false)
    onBlur && onBlur(inputRef.current?.value)
  }

  useEffect(() => {
    registerField({
      name: fieldName,
      ref: inputRef,
      getValue: (ref) => {
        return ref.current.value
      },
      setValue: (ref, value) => {
        ref.current.value = value
      },
      clearValue: (ref) => {
        ref.current.value = ''
      },
    })
  }, [fieldName, registerField])

  useEffect(() => {
    if (inputFocused || defaultValue || defaultValue === 0) {
      setIsFocusedOrValid(true)
    } else {
      setIsFocusedOrValid(false)
    }
  }, [inputFocused, defaultValue])

  const showInput = (tp = 'text') => {
    const input =
      {
        number: [
          {
            mask: 'num',
            blocks: {
              num: {
                mask: Number,
                radix: ',',
                mapToRadix: [','], // accepts both '.' and ',' but maps to '.'
                scale: 2,
                signed: false,
                thousandsSeparator: '.',
                padFractionalZeros: false,
                normalizeZeros: true,
                min: 0,
                max: Number.MAX_SAFE_INTEGER,
              },
            },
          },
        ],
        numberMaxFiveHundred: [
          {
            mask: 'num',
            blocks: {
              num: {
                mask: Number,
                radix: ',',
                mapToRadix: [','], // accepts both '.' and ',' but maps to '.'
                scale: 2,
                signed: false,
                thousandsSeparator: '.',
                padFractionalZeros: false,
                normalizeZeros: true,
                min: 0,
                max: 500,
              },
            },
          },
        ],
        money: [
          {
            mask: 'R$ num',
            blocks: {
              num: {
                mask: Number,
                radix: ',',
                mapToRadix: [','], // accepts both '.' and ',' but maps to '.'
                scale: 2,
                signed: false,
                thousandsSeparator: '.',
                padFractionalZeros: true,
                normalizeZeros: true,
                min: 0,
                max: Number.MAX_SAFE_INTEGER,
              },
            },
          },
        ],
        text: [null],
        cpf: ['000.000.000-00'],
        rg: ['00.000.000-00'],
        cnpj: ['00.000.000/0000-00'],
        cell: ['(00) 00000-0000'],
        cellWithInternational: ['+55 (00) 00000-0000'],
        cep: ['00000-000'],
        deadline: ['00 meses'],
        jurosaa: [
          {
            mask: 'num',
            blocks: {
              num: {
                mask: Number,
                scale: 2,
                signed: false,
                radix: ',',
                mapToRadix: [','],
                min: 0,
                max: 100,
                normalizeZeros: true,
                padFractionalZeros: true,
                suffix: '% a.a.',
              },
            },
          },
        ],
        jurosPercent: [
          {
            mask: 'num %',
            lazy: false,
            blocks: {
              num: {
                mask: Number,
                scale: 2,
                signed: false,
                radix: ',',
                mapToRadix: [','],
                min: 0,
                max: 100,
                normalizeZeros: true,
                padFractionalZeros: true,
              },
            },
          },
        ],
        jurosAno: [
          {
            mask: 'num % \\a.\\a. ', // Blibioteca não reconhece letra a / Para aceitar a letra a, é necessário colocar \\a
            lazy: false,
            blocks: {
              num: {
                mask: Number,
                scale: 2,
                signed: false,
                radix: ',',
                mapToRadix: [','],
                min,
                max,
                normalizeZeros: true,
                padFractionalZeros: true,
              },
            },
          },
        ],
        agency: ['000000000000000000'],
        account: ['000000000000000000'],
        months: [
          {
            lazy: false,
            mask: 'num meses',
            blocks: {
              num: {
                mask: Number,
                scale: 0,
                signed: false,
                min,
                max,
                normalizeZeros: true,
                padFractionalZeros: false,
                // step,
              },
            },
          },
        ],
        percent: [
          {
            mask: Number,
            scale: 2,
            signed: false,
            radix: '.',
            mapToRadix: ['.'],
            min: 0,
            max: 100,
            normalizeZeros: true,
            padFractionalZeros: true,
            suffix: '%',
          },
        ],

        birthday: [
          'd/m/Y',
          {
            d: {
              mask: IMask.MaskedRange,
              from: 1,
              to: 31,
              maxLength: 2,
            },
            m: {
              mask: IMask.MaskedRange,
              from: 1,
              to: 12,
              maxLength: 2,
            },
            Y: {
              mask: IMask.MaskedRange,
              from: 1900,
              to: getCurrent18(),
            },
          },
        ],
        date: [
          'd/m/Y',
          {
            d: {
              mask: IMask.MaskedRange,
              from: 1,
              to: 31,
              maxLength: 2,
            },
            m: {
              mask: IMask.MaskedRange,
              from: 1,
              to: 12,
              maxLength: 2,
            },
            Y: {
              mask: IMask.MaskedRange,
              from: 1900,
              to: 2200,
            },
          },
        ],
      }[tp] || []

    // máscara, regras
    const [mask, blocks] = input

    const alternative =
      tp === 'money' ||
      tp === 'number' ||
      tp === 'jurosaa' ||
      tp === 'jurosPercent' ||
      tp === 'months' ||
      tp === 'jurosAno' ||
      tp === 'numberMaxFiveHundred'

    if (alternative) {
      return (
        <MaskedInput
          onFocus={handleFocus}
          ref={inputRef}
          mask={input}
          maxLength={maxLength}
          onBlur={handleBlur}
          disabled={disabled}
          data-testid={dataTestId}
          onPaste={(e) => {
            handleChange(e)
          }}
          placeholder=""
          className="masked-input"
          name={name}
          defaultValue={defaultValue}
          error={inputError}
          unmask
          // step={step}
          lazy={false}
          commitRaw
          key={uniqKey}
          onAccept={(value) => {
            clearError()
            handleChange(value)
          }}
          {...rest}
        />
      )
    }

    return (
      <MaskedInput
        commitRaw
        onFocus={handleFocus}
        ref={inputRef}
        maxLength={maxLength}
        data-testid={dataTestId}
        onChange={(e) => {
          clearError()
          handleChange(e)
        }}
        onPaste={(e) => {
          clearError()
          handleChange(e)
        }}
        onBlur={handleBlur}
        placeholder=""
        className="masked-input"
        mask={mask}
        blocks={blocks}
        defaultValue={defaultValue}
        name={name}
        disabled={disabled}
        error={inputError}
        key={uniqKey}
        {...rest}
      />
    )
  }

  useEffect(() => {
    setInputError(error)
  }, [error])

  useEffect(() => {
    setInputError(customError)
  }, [customError])

  return (
    <InputContainer small={small} large={large} customWith={customWith}>
      <div className="relative">
        <Placeholder isFocusedOrValid={isFocusedOrValid} isDisabled={disabled}>
          {placeholder}
          {hasAsterisk && <span className="asterisk"> *</span>}
        </Placeholder>
        <Loading loading={loading}>
          <img className="loading-img" src={loadingOrange} alt="loading" />
        </Loading>
        <Row>{showInput(type)}</Row>
      </div>

      <InputError
        {...rest}
        error={inputError}
        disabled={disabled}
        data-testid={`${dataTestId}-error`}
      >
        <img
          className="img-error"
          src={Icon('alert_circle.svg')}
          alt="error icon"
        />
        {inputError}
      </InputError>
    </InputContainer>
  )
}
